import { GRID_SIZE, MUSHROOM_RARITY } from "../config";
import { BIOME, type Biome, type BiomeMap } from "../types";

const BIOME_ORDER: Biome[] = [
    BIOME.Icy,
    BIOME.ColdForest,
    BIOME.Taiga,
    BIOME.Temperate,
    BIOME.Humid,
    BIOME.Swamp,
    BIOME.Savanna,
    BIOME.Desert,
    BIOME.Mesa,
];

/**
 * Hash seed → integer in [0, 2^32). Same seed always picks same biome,
 * different adjacent seeds pick different biomes (no clustering).
 */
export function hashSeed(seed: number): number {
    let h = (seed | 0) ^ 0x9e3779b9;
    h = Math.imul(h ^ (h >>> 16), 0x7feb352d);
    h = Math.imul(h ^ (h >>> 15), 0x846ca68b);
    h ^= h >>> 16;
    return h >>> 0;
}

export function pickBiomeFromSeed(seed: number): Biome {
    if (hashSeed(seed + 0x517e5) % MUSHROOM_RARITY === 0) return BIOME.Mushroom;
    return BIOME_ORDER[hashSeed(seed) % BIOME_ORDER.length];
}

/**
 * Whole render gets a single biome derived from the seed. Constant temp/humid
 * fields are kept so downstream modules (snow layer, decorations) can still
 * read per-cell values without a special case.
 */
export function generateBiomes(seed: number, size = GRID_SIZE): BiomeMap {
    const biomeKind = pickBiomeFromSeed(seed);

    // Synthetic temp/humid that matches the chosen biome's natural climate.
    // Currently informational only, kept on the BiomeMap for future use.
    const climate: Record<Biome, { t: number; h: number }> = {
        [BIOME.Icy]: { t: 0.10, h: 0.50 },
        [BIOME.ColdForest]: { t: 0.30, h: 0.55 },
        [BIOME.Taiga]: { t: 0.32, h: 0.75 },
        [BIOME.Temperate]: { t: 0.55, h: 0.50 },
        [BIOME.Humid]: { t: 0.55, h: 0.85 },
        [BIOME.Swamp]: { t: 0.50, h: 0.65 },
        [BIOME.Savanna]: { t: 0.78, h: 0.40 },
        [BIOME.Desert]: { t: 0.85, h: 0.15 },
        [BIOME.Mesa]: { t: 0.88, h: 0.22 },
        [BIOME.Mushroom]: { t: 0.55, h: 0.90 },
    };
    const { t, h } = climate[biomeKind];

    const temp: number[][] = Array.from({ length: size }, () => Array(size).fill(t));
    const humid: number[][] = Array.from({ length: size }, () => Array(size).fill(h));
    const biome: Biome[][] = Array.from({ length: size }, () => Array<Biome>(size).fill(biomeKind));

    return { temp, humid, biome };
}
